import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaSave, FaFileExport } from 'react-icons/fa';
import CoverLetterForm from './CoverLetterForm';
import CoverLetterPreview from './CoverLetterPreview';
import { exportToPDF } from '../../utils/pdfExport';
import { useDocumentSave } from '../../hooks/useDocumentSave';
import { useAuth } from '../../hooks/useAuth';

const CoverLetterBuilder = () => {
  const { currentUser } = useAuth();
  const { saveDocument } = useDocumentSave();
  const [data, setData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      address: '',
      date: new Date().toISOString().split('T')[0]
    },
    jobInfo: {
      companyName: '',
      jobTitle: '',
      hiringManager: '',
      companyAddress: '',
      jobDescription: ''
    },
    content: {
      introduction: '',
      body: '',
      conclusion: '',
      signature: ''
    },
    template: 'modern'
  });

  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpdateData = (section, newData) => {
    setData(prev => ({
      ...prev,
      [section]: newData
    }));
  };

  const handleTemplateChange = (template) => {
    setData(prev => ({
      ...prev,
      template
    }));
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const loadingToast = toast.loading('Exporting your cover letter...');
      await exportToPDF(
        'cover-letter-preview',
        `${data.personalInfo.fullName || 'Cover-Letter'}-${new Date().toISOString().split('T')[0]}.pdf`
      );
      toast.dismiss(loadingToast);
      toast.success('Cover Letter exported successfully!');
    } catch (error) {
      console.error('Error exporting cover letter:', error);
      toast.error('Failed to export cover letter. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleSaveCoverLetter = async () => {
    if (!currentUser) {
      toast.error('Please sign in to save your cover letter');
      return;
    }

    try {
      setSaving(true);
      const title = `${data.jobInfo.companyName || 'Untitled'} - ${data.jobInfo.jobTitle || 'Cover Letter'}`;
      await saveDocument('coverLetter', data, title);
      toast.success('Cover letter saved successfully!');
    } catch (error) {
      console.error('Error saving cover letter:', error);
      toast.error('Failed to save cover letter. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              Create Your Cover Letter
            </h1>
            <div className="flex space-x-4">
              <button
                onClick={handleSaveCoverLetter}
                disabled={saving}
                className="px-6 py-2.5 flex items-center gap-2 text-sm font-medium rounded-lg
                         bg-blue-600/10 text-blue-400 border border-blue-500/20
                         hover:bg-blue-600/20 hover:border-blue-500/30 transition-all duration-200
                         focus:outline-none focus:ring-2 focus:ring-blue-500/30
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSave className={`text-lg ${saving ? 'animate-spin' : ''}`} />
                {saving ? 'Saving...' : 'Save Cover Letter'}
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="px-6 py-2.5 flex items-center gap-2 text-sm font-medium rounded-lg
                         bg-green-600/10 text-green-400 border border-green-500/20
                         hover:bg-green-600/20 hover:border-green-500/30 transition-all duration-200
                         focus:outline-none focus:ring-2 focus:ring-green-500/30
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaFileExport className="text-lg" />
                {exporting ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-1/2">
              <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 shadow-xl rounded-xl p-6">
                <CoverLetterForm 
                  data={data} 
                  onUpdateData={handleUpdateData}
                  onTemplateChange={handleTemplateChange}
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="sticky top-6">
                <div id="cover-letter-preview" className="bg-gray-900/50 backdrop-blur-xl border border-white/10 shadow-xl rounded-xl p-6">
                  <CoverLetterPreview data={data} template={data.template} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverLetterBuilder;
